import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Cross, ArrowRight, Loader } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", attempts: 0 });
  const timerRef = useRef(null);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/session/${sessionId}`);
        if (r.data.activated) {
          await refresh();
          setState({ status: "success", data: r.data });
          return;
        }
        if (attempts < 6) {
          timerRef.current = setTimeout(poll, 2000);
        } else {
          setState({ status: "pending", data: r.data });
        }
      } catch {
        if (attempts < 6) timerRef.current = setTimeout(poll, 2000);
        else setState({ status: "error" });
      }
    };
    poll();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-white flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 shadow-xl p-8 text-center" data-testid="payment-success">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <div className="h-10 w-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
            <Cross className="h-5 w-5" strokeWidth={2.5} />
          </div>
          <span className="font-head text-lg font-extrabold">FarmaPresenze</span>
        </Link>

        {state.status === "checking" && (
          <>
            <Loader className="h-10 w-10 text-emerald-600 mx-auto animate-spin" />
            <h1 className="font-head text-2xl font-extrabold mt-4">Conferma in corso...</h1>
            <p className="text-sm text-gray-600 mt-2">Stiamo verificando il pagamento con Stripe.</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
            <h1 className="font-head text-2xl font-extrabold mt-4">Benvenuto in FarmaPresenze!</h1>
            <p className="text-sm text-gray-600 mt-3">
              {state.data.subscription_status === "trialing" ? "La tua prova gratuita di 30 giorni è attiva. Oggi 0,00€. Puoi disdire gratis dalla dashboard prima della scadenza, senza addebiti." : "Il tuo abbonamento è attivo. Puoi gestire il rinnovo dalla pagina Fatturazione."}
            </p>
            <Link
              to="/admin"
              data-testid="payment-success-continue"
              className="mt-6 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 w-full"
            >
              Vai al pannello <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
        {state.status === "pending" && (
          <>
            <Loader className="h-14 w-14 text-amber-500 mx-auto" />
            <h1 className="font-head text-2xl font-extrabold mt-4" data-testid="payment-success-pending-title">Conferma in attesa</h1>
            <p className="text-sm text-gray-600 mt-3">
              Il pagamento è in fase di verifica presso Stripe. Puoi aggiornare la pagina tra qualche secondo o proseguire: attiveremo l'abbonamento appena riceviamo la conferma.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <button
                onClick={() => window.location.reload()}
                data-testid="payment-success-refresh"
                className="h-12 px-6 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 w-full"
              >
                Riprova verifica
              </button>
              <Link to="/admin/fatturazione" className="h-12 px-6 rounded-lg border border-gray-200 text-gray-700 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-gray-50 w-full">
                Vai a Fatturazione
              </Link>
            </div>
          </>
        )}
        {state.status === "error" && (
          <>
            <h1 className="font-head text-2xl font-extrabold mt-4">Verifica non riuscita</h1>
            <p className="text-sm text-gray-600 mt-3">Non siamo riusciti a confermare il pagamento. Riprova o contatta il supporto.</p>
            <Link to="/prezzi" className="mt-6 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg border border-gray-300 text-gray-800 text-sm font-semibold hover:bg-gray-50 w-full">
              Torna ai piani
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
